import { cn } from '@/utils/tailwind-util';
/* eslint-disable react-hooks/rules-of-hooks */
import { observer } from 'mobx-react';
import { FormEvent, MouseEvent, useEffect, useState } from 'react';

import { Select, SelectOption } from '@/components/Select';

import { SubInfo } from '../constants';
import { RemarkInfoItem, RemarkOptionItem } from '../dto';
import { useVenueRemarkStore } from '../store';

interface Props {
  subInfo: SubInfo;
}

export default observer(function RemarkSubInfo({ subInfo }: Props) {
  return (
    <>
      {subInfo
        .slice(0, 2)
        .map(({ key, label }: { key: string; label: React.ReactNode }) => (
          <GeneratedInfo key={key} dataKey={key} label={label} />
        ))}
    </>
  );
});

interface GeneratedInfoProps {
  dataKey: string;
  label: React.ReactNode;
}
const GeneratedInfo = observer(({ dataKey, label }: GeneratedInfoProps) => {
  const store = useVenueRemarkStore();
  if (!(store.venueRemark && store.venueRemarkFieldInfo)) return <></>;
  const targetData = store.getData(dataKey);
  const isDefaultInfo = typeof targetData === 'string' || targetData === null;

  return (
    <div className="mt-3 flex items-center text-[#171717]">
      <span className="first-of-type:flex first-of-type:h-14 first-of-type:min-w-[120px] first-of-type:items-center first-of-type:text-[#8b8b8b]">
        {label}
      </span>
      {isDefaultInfo ? (
        <InputInfo dataKey={dataKey} />
      ) : (
        <SelectInfo dataKey={dataKey} />
      )}
    </div>
  );
});

interface InputInfoProps {
  dataKey: string;
}

const INPUT_STYLE = {
  true: 'flex h-full w-full flex-1 border-b bg-white px-4 focus:outline-none rounded border',
  false: 'flex h-full w-full flex-1 border-b bg-white px-4 focus:outline-none',
};

const InputInfo = observer(({ dataKey }: InputInfoProps) => {
  const store = useVenueRemarkStore();
  const targetData = store.getData(dataKey);
  const getInitialInputValue = () => {
    if (targetData) return targetData as string;
    return '';
  };
  useEffect(() => {
    setInputValue(getInitialInputValue());
  }, [store.venueRemark]);

  const [inputValue, setInputValue] = useState(getInitialInputValue());

  const handleInputChange = (e: FormEvent<HTMLInputElement>) => {
    setInputValue(e.currentTarget.value);
    store.venueRemark[dataKey] = e.currentTarget.value || null;
  };
  return (
    <input
      className={cn(INPUT_STYLE[store.editMode.toString()])}
      disabled={!store.editMode}
      value={inputValue}
      onChange={handleInputChange}
    />
  );
});

interface SelectInfoProps {
  dataKey: string;
}

const SelectInfo = observer(({ dataKey }: SelectInfoProps) => {
  const store = useVenueRemarkStore();
  const targetData = store.getData(dataKey);
  const selectOptionData = store.venueRemarkFieldInfo
    .find((ele: RemarkInfoItem) => ele.code === store.convertKeyToCode(dataKey))
    ?.options.map((option: RemarkOptionItem) => {
      return {
        value: JSON.stringify(option),
        label: option.title,
      };
    }) as SelectOption[];

  const [currentOption, setCurrentOption] = useState(
    selectOptionData.find(
      (optionData: SelectOption) =>
        JSON.parse(optionData.value as string)?.code ===
        (targetData as RemarkOptionItem)?.code
    )
  );

  const handleSelectBoxClick = (e: MouseEvent<HTMLSelectElement>) => {
    e.currentTarget.scrollIntoView({ behavior: 'smooth' });
  };
  const handleSelect = (selectedOption: SelectOption) => {
    setCurrentOption(selectedOption);
    store.venueRemark[dataKey] = JSON.parse(
      selectedOption.value as string
    ).code;
  };

  return (
    <>
      <Select
        id={dataKey}
        disabled={!store.editMode}
        options={selectOptionData}
        onSelect={handleSelect}
        onClick={handleSelectBoxClick}
        placeholder={currentOption!.label}
        className="-translate-x-1 !text-[#000] disabled:cursor-not-allowed hover:disabled:border-[#d1d5db] w-60"
        containerStyle={'w-60'}
      />
      {JSON.parse(currentOption!.value as string).remarksField && (
        <div className="ml-5 flex h-full w-1/2 flex-1">
          <span className="first-of-type:flex first-of-type:h-14 first-of-type:w-[120px] first-of-type:items-center first-of-type:text-[#8b8b8b]">
            {JSON.parse(currentOption!.value as string).remarksField.title}
          </span>
          <InputInfo
            dataKey={store.convertCodeToKey(
              JSON.parse(currentOption!.value as string).remarksField.code
            )}
          />
        </div>
      )}
    </>
  );
});
